import React, { useState } from 'react';
import { Card, CardBody, Button, Chip } from '@heroui/react';
import {
  Lightning,
  Shower,
  Thermometer,
  Elevator,
  ShieldCheck,
  Package,
  ArrowRight,
  Check,
} from '@phosphor-icons/react';
import { AssetCategoryTemplate, ASSET_CATEGORY_TEMPLATES } from 'shared/config/assetTemplates';
import { AssetScope } from 'shared/types/asset';

interface CategoryTemplateGalleryProps {
  onSelectTemplate: (template: AssetCategoryTemplate) => void;
  onCustomStart: () => void;
}

// Icon mapping for dynamic rendering
const ICON_MAP: Record<string, any> = {
  Lightning,
  Shower,
  Thermometer,
  Elevator,
  ShieldCheck,
  Package,
};

// Scope translations
const SCOPE_LABELS: Record<AssetScope, string> = {
  [AssetScope.BUILDING]: 'Gebouw',
  [AssetScope.UNIT]: 'Unit',
  [AssetScope.ROOM]: 'Ruimte',
  [AssetScope.PORTABLE]: 'Draagbaar',
};

export const CategoryTemplateGallery: React.FC<CategoryTemplateGalleryProps> = ({
  onSelectTemplate,
  onCustomStart,
}) => {
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);

  const toggleExpanded = (id: string) => {
    setExpandedTemplate(expandedTemplate === id ? null : id);
  };

  const renderIcon = (iconName: string, iconColor: string, size = 20) => {
    const IconComponent = ICON_MAP[iconName];
    if (!IconComponent) return null;
    return <IconComponent size={size} weight="duotone" className={iconColor} />;
  };

  const getScopeChipColor = (scope: AssetScope): 'primary' | 'success' | 'warning' | 'default' => {
    const colorMap: Record<AssetScope, 'primary' | 'success' | 'warning' | 'default'> = {
      [AssetScope.BUILDING]: 'primary',
      [AssetScope.UNIT]: 'success',
      [AssetScope.ROOM]: 'warning',
      [AssetScope.PORTABLE]: 'default',
    };
    return colorMap[scope] || 'default';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          Kies een template
        </h2>
        <p className="text-sm text-gray-600">
          Selecteer een vooraf ingestelde categorie of start met een lege categorie
        </p>
      </div>

      {/* Templates List */}
      <div className="space-y-2">
        {ASSET_CATEGORY_TEMPLATES.map((template) => {
          const isExpanded = expandedTemplate === template.id;

          return (
            <Card
              key={template.id}
              className="border border-gray-200 hover:border-moss-primary hover:shadow-sm transition-all"
            >
              <CardBody className="p-3">
                {/* Main Row */}
                <div className="flex items-center gap-3">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    {renderIcon(template.phosphorIcon, template.iconColor, 24)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-gray-900">
                        {template.name}
                      </h3>
                      <span className="text-xs text-gray-500">
                        {template.types.length} types
                        {template.fields && template.fields.length > 0 && (
                          <>, {template.fields.length} velden</>
                        )}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 line-clamp-1">
                      {template.description}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="light"
                      isIconOnly
                      onPress={() => toggleExpanded(template.id)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <ArrowRight
                        size={16}
                        weight="bold"
                        className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                      />
                    </Button>
                    <Button
                      size="sm"
                      color="primary"
                      variant="flat"
                      onPress={() => onSelectTemplate(template)}
                    >
                      Gebruik
                    </Button>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                    {/* Types */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 mb-2 flex items-center gap-1">
                        <Check size={14} weight="bold" className="text-moss-primary" />
                        Types ({template.types.length})
                      </h4>
                      <div className="flex flex-wrap gap-1.5">
                        {template.types.map((type, idx) => (
                          <Chip
                            key={idx}
                            size="sm"
                            variant="flat"
                            color={getScopeChipColor(type.scopes?.[0] || AssetScope.UNIT)}
                            className="text-xs"
                          >
                            {type.name} ({type.scopes?.map(s => SCOPE_LABELS[s]).join(', ')})
                          </Chip>
                        ))}
                      </div>
                    </div>

                    {/* Fields */}
                    {template.fields && template.fields.length > 0 && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-700 mb-2 flex items-center gap-1">
                          <Check size={14} weight="bold" className="text-blue-600" />
                          Velden ({template.fields.length})
                        </h4>
                        <div className="grid grid-cols-2 gap-1.5">
                          {template.fields.map((field, idx) => (
                            <div
                              key={idx}
                              className="text-xs text-gray-600 flex items-center gap-1"
                            >
                              <span className="w-1 h-1 rounded-full bg-gray-400" />
                              {field.displayName}
                              {field.isRequired && (
                                <span className="text-red-500">*</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Info */}
                    <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
                      Na het selecteren kun je alle instellingen nog aanpassen
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          );
        })}

        {/* Custom Category Option */}
        <Card
          isPressable
          onPress={onCustomStart}
          className="border-2 border-dashed border-gray-300 hover:border-moss-primary hover:bg-gray-50 transition-all"
        >
          <CardBody className="p-3">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <Package size={24} weight="duotone" className="text-gray-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 mb-0.5">
                  Eigen Categorie
                </h3>
                <p className="text-xs text-gray-600">
                  Start met een lege categorie en bouw alles zelf op
                </p>
              </div>
              <Button
                size="sm"
                color="default"
                variant="flat"
                onPress={onCustomStart}
              >
                Start
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};
